import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ControlErrorDirective, LabelValueComponent, UIBlockerService } from '@howdeniberia/core-front';
import { Observable, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { IResponseMessage } from 'src/app/core/models/api/sane-project/arpa-model';
import { CalculatorProjectService } from 'src/app/core/services/api/calculator/calculator-project.service';
import { BonusPipe } from 'src/app/core/services/api/shared/bonus.pipe';
import { SharedService } from 'src/app/core/services/api/shared/shared.service';
import { normalizeTraceHtml } from 'src/app/shared/utils';
import { ProjectViewModel } from '../../models/project.viewmodel';
import { CalculatorMapper } from '../mappers/calculator.mapper';
import { CalculatorProductViewModel, lifePresets } from '../models/calculator-product.viewmodel';
import { NumberMaxDirective } from '../models/number-max.directive';

@Component({
  selector: 'howden-calculator-life',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDividerModule,
    ControlErrorDirective,
    LabelValueComponent,
    BonusPipe,
    NumberMaxDirective
  ],
  templateUrl: './calculator-life.component.html',
  styleUrls: ['./calculator-life.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CalculatorLifeComponent implements OnDestroy {
  private fb = inject(FormBuilder);
  private sharedSrv = inject(SharedService);
  private uiBlocker = inject(UIBlockerService);
  private calcSrv = inject(CalculatorProjectService);
  private cdr = inject(ChangeDetectorRef);
  private sanitizer = inject(DomSanitizer);

  form: FormGroup;

  lifeAllianz = new CalculatorProductViewModel(lifePresets['allianz']);
  lifeAxa = new CalculatorProductViewModel(lifePresets['axa']);
  lifeStaLucia = new CalculatorProductViewModel(lifePresets['staLucia']);
  lifeSurne = new CalculatorProductViewModel(lifePresets['surne']);
  lifeSurneLifePro = new CalculatorProductViewModel(lifePresets['surneLifePro']);
  lifeHelvetia = new CalculatorProductViewModel(lifePresets['helvetia']);
  lifePrebal = new CalculatorProductViewModel(lifePresets['prebal']);
  lifePrebalInvPro = new CalculatorProductViewModel(lifePresets['prebalInvPro']);
  lifeZurich = new CalculatorProductViewModel(lifePresets['zurich']);

  project!: ProjectViewModel;
  private destroy$ = new Subject<void>();

  private readonly vmMap = {
    allianz: this.lifeAllianz,
    axa: this.lifeAxa,
    staLucia: this.lifeStaLucia,
    surne: this.lifeSurne,
    surneLifePro: this.lifeSurneLifePro,
    helvetia: this.lifeHelvetia,
    prebal: this.lifePrebal,
    prebalInvPro: this.lifePrebalInvPro,
    zurich: this.lifeZurich
  } as const;

  canSeeTrace = false;

  constructor() {
    this.form = this.fb.group({
      lifeAllianz: this.lifeAllianz.form,
      lifeAxa: this.lifeAxa.form,
      lifeStaLucia: this.lifeStaLucia.form,
      lifeSurne: this.lifeSurne.form,
      lifeSurneLifePro: this.lifeSurneLifePro.form,
      lifeHelvetia: this.lifeHelvetia.form,
      lifePrebal: this.lifePrebal.form,
      lifePrebalInvPro: this.lifePrebalInvPro.form,
      lifeZurich: this.lifeZurich.form
    });

    this.canSeeTrace = this.sharedSrv.getCanSeeTrace();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public shouldShowZeroState(vm: CalculatorProductViewModel): boolean {
    return !vm.hasAnyPositiveNumeric();
  }

  public showTrace(vm: CalculatorProductViewModel): boolean {
    return !!this.canSeeTrace && vm.hasAnyPositiveNumeric();
  }

  public getTraceHtml(vm: CalculatorProductViewModel): SafeHtml | '' {
    if (!this.showTrace(vm) || !vm.trace) { return ''; }
    const normalized = normalizeTraceHtml(vm.trace);
    return this.sanitizer.bypassSecurityTrustHtml(normalized);
  }

  public getMessages(vm: CalculatorProductViewModel): IResponseMessage[] {
    return vm.hasAnyPositiveNumeric() ? (vm.messages ?? []) : [];
  }

  public initFromViewModel(vm: ProjectViewModel['calculatorLife']): void {
    const model = this.sharedSrv.getModel();
    if (!model) { throw new Error('ProjectViewModel not available in SharedService'); }
    this.project = model;

    (Object.entries(this.vmMap) as Array<[keyof typeof this.vmMap, CalculatorProductViewModel]>)
      .forEach(([key, child]) => {
        const src = vm[key];
        child.form.patchValue(src.form.getRawValue(), { emitEvent: false });

        child.bonus = src.bonus ?? 0;
        child.chargeable = !!src.chargeable;
        child.productName = src.productName ?? '';
        child.trace = src.trace ?? '';
        child.maximumCapital = src.maximumCapital ?? 0;
        child.hasTelesubscription = !!src.hasTelesubscription;
        child.telesubscriptionDirect = !!src.telesubscriptionDirect;
        child.telesubscriptionSingleQuestion = !!src.telesubscriptionSingleQuestion;
        child.summaryCapitals = src.summaryCapitals ?? '';
        child.messages = Array.isArray(src.messages) ? [...src.messages] : [];
      });

    this.setupListeners();

    // initial calculation
    this.onInputChangeVAllianz();
    this.onInputChangeVAxa();
    this.onInputChangeVStaLucia();
    this.onInputChangeVSurne();
    this.onInputChangeVSurneLifePro();
    this.onInputChangeVHelvetia();
    this.onInputChangeVPrebal();
    this.onInputChangeVPrebalInvPro();
    this.onInputChangeVZurich();

    this.cdr.detectChanges();
  }

  public flushToViewModel(): void {
    const vm = this.sharedSrv.getModel()!.calculatorLife;
    (Object.entries(this.vmMap) as Array<[keyof typeof this.vmMap, CalculatorProductViewModel]>)
      .forEach(([key, child]) => {
        const tgt = vm[key];

        tgt.form.patchValue(child.form.getRawValue(), { emitEvent: false });

        tgt.bonus = child.bonus ?? 0;
        tgt.chargeable = !!child.chargeable;
        tgt.productName = child.productName ?? '';
        tgt.trace = child.trace ?? '';
        tgt.maximumCapital = child.maximumCapital ?? 0;
        tgt.hasTelesubscription = !!child.hasTelesubscription;
        tgt.telesubscriptionDirect = !!child.telesubscriptionDirect;
        tgt.telesubscriptionSingleQuestion = !!child.telesubscriptionSingleQuestion;
        tgt.summaryCapitals = child.summaryCapitals ?? '';
        tgt.messages = Array.isArray(child.messages) ? [...child.messages] : [];
      });
  }

  private setupListeners(): void {
    const hook = (child: CalculatorProductViewModel, fn: () => void) => {
      child.changes$
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => fn());
    };

    hook(this.lifeAllianz, () => this.onInputChangeVAllianz());
    hook(this.lifeAxa, () => this.onInputChangeVAxa());
    hook(this.lifeStaLucia, () => this.onInputChangeVStaLucia());
    hook(this.lifeSurne, () => this.onInputChangeVSurne());
    hook(this.lifeSurneLifePro, () => this.onInputChangeVSurneLifePro());
    hook(this.lifeHelvetia, () => this.onInputChangeVHelvetia());
    hook(this.lifePrebal, () => this.onInputChangeVPrebal());
    hook(this.lifePrebalInvPro, () => this.onInputChangeVPrebalInvPro());
    hook(this.lifeZurich, () => this.onInputChangeVZurich());
  }

  private recalc(obs$: Observable<any>): void {
    this.uiBlocker.block();
    obs$
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.uiBlocker.unblock();
          this.cdr.markForCheck();
        })
      )
      .subscribe();
  }

  public onInputChangeVAllianz(): void {
    this.recalc(
      CalculatorMapper.calcLifeAllianz(
        this.project,
        this.lifeAllianz,
        req => this.calcSrv.resultBonusVAllianz(req)
      )
    );
  }

  public onInputChangeVAxa(): void {
    this.recalc(
      CalculatorMapper.calcLifeAxa(
        this.project,
        this.lifeAxa,
        req => this.calcSrv.resultBonusVAxa(req)
      )
    );
  }

  public onInputChangeVStaLucia(): void {
    this.recalc(
      CalculatorMapper.calcLifeStaLucia(
        this.project,
        this.lifeStaLucia,
        req => this.calcSrv.resultBonusVStaLucia(req)
      )
    );
  }

  public onInputChangeVSurne(): void {
    this.recalc(
      CalculatorMapper.calcLifeSurne(
        this.project,
        this.lifeSurne,
        req => this.calcSrv.resultBonusVSurne(req)
      )
    );
  }

  public onInputChangeVSurneLifePro(): void {
    this.recalc(
      CalculatorMapper.calcLifeSurneLifePro(
        this.project,
        this.lifeSurneLifePro,
        req => this.calcSrv.resultBonusVSurneLifePro(req)
      )
    );
  }

  public onInputChangeVHelvetia(): void {
    this.recalc(
      CalculatorMapper.calcLifeHelvetia(
        this.project,
        this.lifeHelvetia,
        req => this.calcSrv.resultBonusVHelvetia(req)
      )
    );
  }

  public onInputChangeVPrebal(): void {
    this.recalc(
      CalculatorMapper.calcLifePrebal(
        this.project,
        this.lifePrebal,
        req => this.calcSrv.resultBonusVPrebal(req)
      )
    );
  }

  public onInputChangeVPrebalInvPro(): void {
    this.recalc(
      CalculatorMapper.calcLifePrebalInvPro(
        this.project,
        this.lifePrebalInvPro,
        req => this.calcSrv.resultBonusVPrebalInvPro(req)
      )
    );
  }

  public onInputChangeVZurich(): void {
    this.recalc(
      CalculatorMapper.calcLifeZurich(
        this.project,
        this.lifeZurich,
        req => this.calcSrv.resultBonusVZurich(req)
      )
    );
  }
}
